import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';

import { queryKeys } from '@/hooks/queries/query-keys';
import { useConfigQuery } from '@/hooks/queries/use-config-query';
import { settingsQueryOptions } from '@/hooks/queries/use-settings-query';
import { initializeFirebase } from '@/lib/firebase';
import { isInvalidTenantError } from '@/lib/http-errors';
import { InvalidTenantPage } from '@/pages/InvalidTenantPage';
import { QueryErrorPage } from '@/pages/QueryErrorPage';
import { useSessionStore } from '@/stores/session';

/**
 * ConfigGate blocks rendering until config is loaded, then initializes
 * Firebase with the values returned from GET /config. Firebase can only
 * be accessed (via getFirebaseAuth) inside children, because ConfigGate
 * guarantees initialization has run before they mount.
 *
 * Place AFTER SessionProvider in the tree so session init can start
 * in parallel with config loading.
 */
export function ConfigGate({ children }: { children: ReactNode }) {
  const query = useConfigQuery();
  const { data, isError, error, failureReason } = query;

  if (isInvalidTenantError(error)) {
    return <InvalidTenantPage />;
  }

  // A failed background refetch keeps the loaded app; only a first load shows
  // the error. failureReason is set while a 429 is being retried.
  if (data === undefined) {
    if (isError || failureReason) {
      return <QueryErrorPage query={query} />;
    }

    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Idempotent: repeated calls after first render are no-ops.
  initializeFirebase(data.policy.firebase);

  return <>{children}</>;
}

/**
 * SettingsPrefetcher prefetches settings when the session becomes active, and
 * drops the cached queries when the signed-in admin signs out or changes.
 * Place outside ConfigGate so the prefetch fires as soon as auth completes,
 * without waiting for config to finish loading.
 */
export function SettingsPrefetcher() {
  const queryClient = useQueryClient();
  const sessionStatus = useSessionStore((s) => s.status);
  const prevSessionStatus = useRef(sessionStatus);
  const principal = useSessionStore((s) => (s.user ? `${s.user.tenantId}/${s.user.id}` : null));
  const prevPrincipal = useRef(principal);

  // No query key carries the tenant or admin, so the cache must not outlive the
  // admin it was fetched for. A logout unmounts the dashboard, so the cache is
  // simply dropped; config is public and invalidated at login. Another tab
  // signing in as someone else swaps the user here with no logout, while the
  // dashboard is live: its cache, filter stores and hydration flag all belong
  // to the last admin, so the tab reloads rather than refetch on their filters.
  useEffect(() => {
    const prev = prevPrincipal.current;
    prevPrincipal.current = principal;
    if (prev === null || prev === principal) return;
    if (principal !== null) {
      window.location.reload();
      return;
    }
    queryClient.removeQueries({
      predicate: ({ queryKey }) => queryKey[0] !== queryKeys.config.root[0],
    });
  }, [principal, queryClient]);

  useEffect(() => {
    const prev = prevSessionStatus.current;
    prevSessionStatus.current = sessionStatus;

    if (sessionStatus === 'active' && prev !== 'active') {
      if (prev !== 'initializing') {
        // Login transition (e.g. unauthenticated → active) — re-fetch config with auth
        queryClient.invalidateQueries({ queryKey: queryKeys.config.root });
      }
      // Initial load (initializing → active): config fires for the first time with auth, no invalidation needed
      queryClient.prefetchQuery(settingsQueryOptions);
    }
  }, [sessionStatus, queryClient]);

  return null;
}
